import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { Bot, ChevronRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAiApi } from '../ai-employee/hooks/use-ai-api';
import { REPLY_MODE_STATUS_NAMES } from '../ai-employee/components/mode-labels';
import {
	currentReplyMode,
	replyModesFrom,
	type ReplyMode,
	type ReplyModePair,
	type ReplyStatus,
} from '../ai-employee/components/reply-modes';
import {
	Tooltip,
	TooltipContent,
	TooltipTrigger,
} from '@/components/ui/tooltip';
import { useSidebar } from '@/components/ui/sidebar';
import {
	AI_OFFER_SYNC_EVENT,
	hasAiEmployeeProduct,
	isAiOfferDismissed,
} from '@/utils/ai-employee-access';
import { isSetupInvited } from '../ai-employee/setup-gate';
import { isSetupMarkedCompleted } from '../ai-employee/setup/setup-storage';
import './ai-employee-widget.css';

interface AiSettingsResponse {
	ai: {
		enabled?: boolean;
		mode?: string;
		delayMinutes?: number;
		operatingHoursMode?: string;
		openHoursMode?: string;
		closedHoursMode?: string;
		customInstructions?: string;
		topicRules?: unknown[];
		objectives?: unknown[];
		setup?: { status?: string; step?: string; preexisting?: boolean } | null;
	};
	/** Whether the business is open now and the mode in effect, worked out by the server. */
	replyStatus?: ReplyStatus;
}

// The status flips at opening and closing time, so the settings are re-read now and then.
const SETTINGS_REFRESH_MS = 5 * 60 * 1000;

interface UsageResponse {
	impact: {
		avgResponseTimeSec: number | null;
		responseRate: number;
	};
}

function formatResponseTime(seconds: number | null): string {
	if (seconds === null) return '—';
	if (seconds < 60) return `${seconds}s`;
	const mins = Math.floor(seconds / 60);
	const secs = seconds % 60;
	return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
}

function getModeLabel(mode?: string): string {
	return REPLY_MODE_STATUS_NAMES[mode ?? 'off'] ?? REPLY_MODE_STATUS_NAMES.off;
}

/** "Off until you close" when the AI is off now but replies in the other period. */
function getOffUntilText(
	currentMode: ReplyMode,
	period: ReplyStatus['period'],
	modes: ReplyModePair,
): string | null {
	if (currentMode !== 'off') return null;
	if (period === 'open' && modes.closedHoursMode !== 'off') return 'Off until you close';
	if (period === 'closed' && modes.openHoursMode !== 'off') return 'Off until you open';
	return null;
}

function getStatusText(
	mode: ReplyMode,
	offUntil: string | null,
	pendingEscalationCount: number,
	avgResponseTime: number | null,
	delayMinutes?: number,
): string {
	if (offUntil) return offUntil;
	if (mode === 'off') return REPLY_MODE_STATUS_NAMES.off;
	if (pendingEscalationCount > 0)
		return `${pendingEscalationCount} escalation${pendingEscalationCount !== 1 ? 's' : ''}`;
	if (mode === 'auto')
		return avgResponseTime !== null
			? `Replying · ${formatResponseTime(avgResponseTime)} avg`
			: 'Replying to messages';
	if (mode === 'delayed') {
		const mins = delayMinutes ?? 10;
		return `Standby · ${mins}min delay`;
	}
	if (mode === 'assist') return 'Drafting replies';
	return REPLY_MODE_STATUS_NAMES.off;
}

export function AiEmployeeWidget() {
	const navigate = useNavigate();
	const { state: sidebarState } = useSidebar();
	const isCollapsed = sidebarState === 'collapsed';

	const auth = useSelector((state: any) => state.auth);
	const businessId = auth?._id;
	const hasAiProduct = hasAiEmployeeProduct(auth);

	const [offerDismissed, setOfferDismissed] = useState(() =>
		isAiOfferDismissed(),
	);

	useEffect(() => {
		const sync = () => setOfferDismissed(isAiOfferDismissed());
		window.addEventListener(AI_OFFER_SYNC_EVENT, sync);
		return () => window.removeEventListener(AI_OFFER_SYNC_EVENT, sync);
	}, []);

	const pendingEscalationCount = useSelector(
		(state: any) => state.aiEmployee?.pendingEscalationCount ?? 0,
	);

	const statusByConversation = useSelector(
		(state: any) => state.aiEmployee?.statusByConversation ?? {},
	);

	const isThinking = useMemo(
		() => Object.values(statusByConversation).some((s) => s === 'thinking'),
		[statusByConversation],
	);

	const { data: settingsData, refetch: refetchSettings } =
		useAiApi<AiSettingsResponse>(
			hasAiProduct && businessId
				? `/api/ai-employee/settings/${businessId}`
				: null,
		);

	const { data: usageData } = useAiApi<UsageResponse>(
		hasAiProduct && businessId
			? `/api/ai-employee/usage/${businessId}?period=7d`
			: null,
	);

	useEffect(() => {
		const handler = () => refetchSettings();
		window.addEventListener('ai-settings-changed', handler);
		return () => window.removeEventListener('ai-settings-changed', handler);
	}, [refetchSettings]);

	// Pick up opening and closing time: re-read every few minutes and when the tab regains focus.
	useEffect(() => {
		if (!hasAiProduct || !businessId) return;
		const refresh = () => refetchSettings();
		const timer = window.setInterval(refresh, SETTINGS_REFRESH_MS);
		window.addEventListener('focus', refresh);
		return () => {
			window.clearInterval(timer);
			window.removeEventListener('focus', refresh);
		};
	}, [refetchSettings, hasAiProduct, businessId]);

	if (!hasAiProduct && offerDismissed) return null;

	// The mode in effect now (open or closed hours), as the server works it out.
	const mode = currentReplyMode(settingsData?.ai, settingsData?.replyStatus);
	const offUntil = getOffUntilText(
		mode,
		settingsData?.replyStatus?.period,
		replyModesFrom(settingsData?.ai),
	);
	const delayMinutes = settingsData?.ai?.delayMinutes;
	const modeLabel = offUntil ?? getModeLabel(mode);
	const avgResponseTime = usageData?.impact?.avgResponseTimeSec ?? null;
	const isActive = mode !== 'off';
	const isDelayed = mode === 'delayed';
	// Setup neither completed nor skipped, and the AI is off: invite the owner to set it up. A
	// business set up before the wizard keeps its usual (paused) status.
	const setupInvited =
		hasAiProduct &&
		isSetupInvited(settingsData?.ai, businessId ? isSetupMarkedCompleted(businessId) : false);

	const handleClick = () => {
		if (!hasAiProduct) return navigate('/ai');
		// Waiting escalations come first, whatever the setup state.
		if (pendingEscalationCount > 0) return navigate('/ai/escalations');
		navigate(setupInvited ? '/ai/start' : '/ai/overview');
	};

	if (!hasAiProduct) {
		if (isCollapsed) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={handleClick}
							aria-label="Enable AI Employee"
							className="mx-auto flex h-8 w-8 items-center justify-center rounded-md text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
						>
							<Sparkles className="size-2" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="right">
						Enable AI Employee
					</TooltipContent>
				</Tooltip>
			);
		}

		return (
			<button
				type="button"
				onClick={handleClick}
				className="flex items-center gap-2 rounded-lg border border-dashed border-primary/30 bg-primary/5 px-2 py-2 text-sm text-primary hover:bg-primary/10 transition-colors max-md:min-h-11 max-md:gap-3 max-md:px-3 max-md:text-[15px]"
			>
				<Sparkles className="size-4 shrink-0 max-md:size-5" />
				<span className="font-medium truncate">Enable AI Employee</span>
			</button>
		);
	}

	if (setupInvited) {
		if (isCollapsed) {
			return (
				<Tooltip>
					<TooltipTrigger asChild>
						<button
							type="button"
							onClick={handleClick}
							aria-label="Set up AI Employee · Takes 60 sec"
							className="relative mx-auto flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent transition-colors"
						>
							<Bot className="size-4" />
							<span className="absolute top-0.5 right-0.5 h-2 w-2 rounded-full border border-background bg-gradient-to-br from-[#7FB2FF] to-[#B094F6]" />
						</button>
					</TooltipTrigger>
					<TooltipContent side="right">AI Employee · Takes 60 sec · Set up</TooltipContent>
				</Tooltip>
			);
		}

		return (
			<button
				type="button"
				onClick={handleClick}
				data-testid="ai-employee-setup"
				className={cn(
					'ai-setup-card group/ai-employee flex min-h-[46px] w-full items-center gap-2.5 rounded-xl px-3 py-1.5 text-left text-sm text-foreground',
					'bg-background dark:bg-sidebar-accent transition-transform duration-200 hover:-translate-y-px motion-reduce:transition-none motion-reduce:hover:translate-y-0',
					'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
				)}
			>
				<div className="relative shrink-0">
					<Bot className="size-5" />
					<span className="absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-background bg-muted-foreground/50 dark:border-sidebar-accent" />
				</div>
				<div className="flex min-w-0 flex-1 flex-col items-start">
					<span className="w-full text-sm leading-tight truncate">AI Employee</span>
					<span className="w-full text-[11px] leading-tight truncate text-muted-foreground">Takes 60 sec</span>
				</div>
				<span className="ml-auto shrink-0 rounded-full bg-foreground px-2.5 py-[5px] text-[11px] font-semibold leading-none text-background shadow-[0_2px_6px_-2px_rgba(0,0,0,0.35)] transition-transform duration-200 group-hover/ai-employee:scale-[1.04] motion-reduce:transition-none">
					Set up
				</span>
			</button>
		);
	}

	if (isCollapsed) {
		return (
			<Tooltip>
				<TooltipTrigger asChild>
					<button
						type="button"
						onClick={handleClick}
						className="relative mx-auto flex h-8 w-8 items-center justify-center rounded-md hover:bg-accent transition-colors"
					>
						<Bot className="size-4" />
						<span
							className={cn(
								'absolute top-0.5 right-0.5 h-2 w-2 rounded-full border border-background',
								isDelayed
									? 'bg-blue-500'
									: isActive
										? 'bg-emerald-500'
										: 'bg-muted-foreground/50',
								isThinking && 'animate-pulse',
							)}
						/>
						{pendingEscalationCount > 0 && (
							<span className="absolute -top-0.5 -right-0.5 flex h-3.5 min-w-3.5 items-center justify-center rounded-full bg-amber-500 text-[8px] font-bold text-white">
								{pendingEscalationCount}
							</span>
						)}
					</button>
				</TooltipTrigger>
				<TooltipContent side="right">
					AI Employee · {modeLabel}
				</TooltipContent>
			</Tooltip>
		);
	}

	const statusText = getStatusText(
		mode,
		offUntil,
		pendingEscalationCount,
		avgResponseTime,
		delayMinutes,
	);

	return (
		<button
			type="button"
			onClick={handleClick}
			className={cn(
				'group/ai-employee flex min-h-[46px] w-full items-center gap-2.5 rounded-xl border px-3 py-1.5 text-left text-sm text-foreground',
				'bg-background shadow-sm hover:shadow-[0_6px_14px_-5px_rgba(0,0,0,0.2)] dark:bg-sidebar-accent dark:hover:shadow-[0_6px_14px_-5px_rgba(0,0,0,0.5)]',
				'transition-[background-color,border-color,box-shadow] duration-200 motion-reduce:transition-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
				isDelayed
					? 'border-blue-700/25 hover:border-blue-700/40 dark:border-blue-400/25 dark:hover:border-blue-400/40'
					: isActive
						? 'border-emerald-800/25 hover:border-emerald-800/40 dark:border-emerald-400/25 dark:hover:border-emerald-400/40'
						: 'border-border hover:border-muted-foreground/40',
			)}
		>
			<div className="relative shrink-0">
				<Bot className="size-5" />
				<span
					className={cn(
						'absolute -bottom-0.5 -right-0.5 h-2 w-2 rounded-full border border-background dark:border-sidebar-accent',
						isDelayed
							? 'bg-blue-500 animate-pulse'
							: isActive
								? 'bg-emerald-500 animate-pulse'
								: 'bg-muted-foreground/50',
					)}
				/>
			</div>
			<div className="flex min-w-0 flex-1 flex-col items-start">
				<span className="w-full text-sm leading-tight truncate">
					AI Employee
				</span>
				<span
					title={statusText}
					className={cn(
						'w-full text-[11px] leading-tight truncate',
						pendingEscalationCount > 0
							? 'text-amber-600 dark:text-amber-400'
							: 'text-muted-foreground',
					)}
				>
					{statusText}
				</span>
			</div>
			<div className="ml-auto flex shrink-0 items-center gap-1.5">
				{pendingEscalationCount > 0 ? (
					<span className="flex h-4 min-w-4 items-center justify-center rounded-full bg-amber-500 text-[9px] font-bold text-white px-1">
						{pendingEscalationCount}
					</span>
				) : (
					<ChevronRight
						className="size-4 text-muted-foreground group-hover/ai-employee:text-foreground group-focus-visible/ai-employee:text-foreground"
						aria-hidden="true"
					/>
				)}
			</div>
		</button>
	);
}
